import { useState, useRef } from 'react';
import { Map, FileSpreadsheet, CheckCircle, AlertTriangle, RefreshCw, Upload } from 'lucide-react';
import { useStore } from '../store/useStore';
import { useVisibleStore } from '../hooks/useVisibleStore';
import { normalizeString } from '../lib/textUtils';
import { readRows, buildPlaces, formatDateFr, type ParseResult } from '../lib/placeImport';

const ACCEPTED = /\.(csv|xlsx|xls|ods|txt)$/i;

/**
 * Importe un programme d'événements et crée les lieux correspondants.
 * Un lieu = un point opérationnel ; ses événements = ses créneaux de check-in.
 */
export function ImportPlaces() {
  const { importPlaces, currentProjectId } = useStore();
  const { categories, points } = useVisibleStore();

  const [parsed, setParsed] = useState<ParseResult | null>(null);
  const [fileName, setFileName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<{ created: number; updated: number } | null>(null);
  const [importing, setImporting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    setError(null);
    setReport(null);
    setParsed(null);
    try {
      const rows = await readRows(file);
      const result = buildPlaces(rows as unknown[][]);
      if (result.places.length === 0) {
        setError("Aucun lieu reconnu dans ce fichier. Vérifiez qu'il contient au moins une colonne « Lieu » et une colonne « Date ».");
        return;
      }
      setFileName(file.name);
      setParsed(result);
    } catch (e) {
      setError('Lecture impossible : ' + (e instanceof Error ? e.message : String(e)));
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    if (ACCEPTED.test(file.name)) handleFile(file);
    else setError('Format non reconnu. Utilisez un fichier .xlsx, .xls ou .csv.');
  };

  const reset = () => {
    setParsed(null);
    setFileName('');
    setError(null);
    setReport(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleImport = async () => {
    if (!parsed) return;
    setImporting(true);
    setError(null);
    const res = await importPlaces(parsed.places, { categoryId: categoryId || undefined });
    setImporting(false);
    if (res.error) setError(res.error);
    else setReport({ created: res.created, updated: res.updated });
  };

  // Lieux déjà présents dans le projet : ils seront mis à jour, pas dupliqués.
  const existingKeys = new Set(points.map(p => normalizeString(p.name)));

  const totalSlots = parsed ? parsed.places.reduce((sum, p) => sum + p.slots.length, 0) : 0;
  const newCount = parsed ? parsed.places.filter(p => !existingKeys.has(p.key)).length : 0;
  const updateCount = parsed ? parsed.places.length - newCount : 0;

  return (
    <div className="flex flex-col flex-1 min-h-0 relative z-10">
      {/* ── En-tête ─────────────────────────────────────────── */}
      <div className="sticky top-0 z-20 bg-[#0A0B0E]/95 backdrop-blur-sm border-b border-[#2D3139]/50 px-5 py-3.5">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-purple-500/15 border border-purple-500/25 flex items-center justify-center flex-none">
              <Map className="w-4 h-4 text-purple-400" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-white">Importer lieu et événements</h2>
              <p className="text-[11px] text-slate-500">
                {parsed
                  ? `${fileName} — ${parsed.places.length} lieu(x), ${totalSlots} créneau(x) de check-in`
                  : 'Crée les lieux et leurs créneaux depuis un fichier de programmation'}
              </p>
            </div>
          </div>
          {parsed && (
            <button
              onClick={reset}
              className="flex items-center gap-1.5 px-3 py-2 bg-[#1A1D23] border border-[#2D3139] text-slate-300 hover:text-white rounded-xl text-xs font-medium transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Réinitialiser
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar p-5">
        {/* ── Bannières ─────────────────────────────────────── */}
        {!currentProjectId && (
          <div className="mb-4 flex items-start gap-2.5 bg-amber-500/10 border border-amber-500/25 text-amber-300 text-xs px-4 py-3 rounded-xl">
            <AlertTriangle className="w-4 h-4 flex-none mt-0.5" />
            <span>Aucun projet actif. Les lieux créés seraient rattachés à l'organisation entière — sélectionnez un projet avant d'importer.</span>
          </div>
        )}

        {error && (
          <div className="mb-4 flex items-start gap-2.5 bg-red-500/10 border border-red-500/25 text-red-400 text-xs px-4 py-3 rounded-xl">
            <AlertTriangle className="w-4 h-4 flex-none mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {report && (
          <div className="mb-4 flex items-center gap-2.5 bg-emerald-500/10 border border-emerald-500/25 text-emerald-400 text-xs px-4 py-3 rounded-xl">
            <CheckCircle className="w-4 h-4 flex-none" />
            <span>
              {report.created} lieu(x) créé(s), {report.updated} mis à jour. Retrouvez-les dans <strong>Op Points</strong>.
            </span>
          </div>
        )}

        {/* ── Zone de dépôt ─────────────────────────────────── */}
        {!parsed ? (
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-colors ${
              dragOver ? 'border-purple-500 bg-purple-500/5' : 'border-[#2D3139] hover:border-slate-500 bg-[#14161B]/50'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls,.ods"
              className="hidden"
              onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
            />
            <FileSpreadsheet className={`w-10 h-10 mx-auto mb-4 ${dragOver ? 'text-purple-400' : 'text-slate-600'}`} />
            <p className="text-sm font-medium text-white mb-1.5">Déposez votre fichier ici</p>
            <p className="text-xs text-slate-500 mb-4">xlsx, xls, csv — la première feuille est lue</p>
            <span className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-xs font-bold transition-colors">
              <Upload className="w-3.5 h-3.5" /> Choisir un fichier
            </span>
            <p className="text-[11px] text-slate-600 mt-5 max-w-md mx-auto leading-relaxed">
              Colonnes reconnues automatiquement : Lieu, Événement, Date, Heure de début, Heure de fin,
              Description, Catégorie, Billet, Quantité max, Lien. Les autres sont ignorées.
            </p>
          </div>
        ) : (
          <>
            {/* ── Catégorie cible ───────────────────────────── */}
            <div className="mb-4 bg-[#14161B] border border-[#2D3139] rounded-xl p-4">
              <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">
                Catégorie des lieux créés
              </label>
              <select
                value={categoryId}
                onChange={e => setCategoryId(e.target.value)}
                className="w-full max-w-sm bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-purple-500"
              >
                <option value="">Aucune catégorie (visible par tous les admins)</option>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <p className="text-[11px] text-slate-500 mt-2">
                Une catégorie restreint la visibilité du lieu à ses managers. Sans catégorie, le lieu est visible par tous les admins.
              </p>
            </div>

            {/* ── Avertissements de lecture ─────────────────── */}
            {parsed.warnings.length > 0 && (
              <div className="mb-4 bg-amber-500/10 border border-amber-500/25 rounded-xl p-4">
                <p className="text-[11px] font-bold text-amber-300 mb-2">
                  {parsed.warnings.length} anomalie(s) détectée(s) — les lignes concernées ont été ignorées
                </p>
                <ul className="space-y-1 max-h-32 overflow-y-auto custom-scrollbar">
                  {parsed.warnings.slice(0, 20).map((w, i) => (
                    <li key={i} className="text-[11px] text-amber-200/80">{w}</li>
                  ))}
                  {parsed.warnings.length > 20 && (
                    <li className="text-[11px] text-amber-200/50">… et {parsed.warnings.length - 20} autre(s)</li>
                  )}
                </ul>
              </div>
            )}

            {/* ── Aperçu ────────────────────────────────────── */}
            <div className="mb-4 flex items-center gap-3 text-[11px]">
              <span className="px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/25 text-emerald-400 font-bold">
                {newCount} à créer
              </span>
              <span className="px-2.5 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/25 text-indigo-400 font-bold">
                {updateCount} à mettre à jour
              </span>
              <span className="px-2.5 py-1 rounded-full bg-[#1A1D23] border border-[#2D3139] text-slate-400 font-bold">
                {totalSlots} créneaux
              </span>
              {parsed.rowsSkipped > 0 && (
                <span className="text-slate-500">{parsed.rowsSkipped} ligne(s) ignorée(s)</span>
              )}
            </div>

            <div className="border border-[#2D3139] rounded-xl overflow-hidden">
              <div className="max-h-[55vh] overflow-y-auto custom-scrollbar">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-[#1A1D23] z-10">
                    <tr className="text-left text-[10px] font-black text-slate-500 uppercase tracking-widest">
                      <th className="px-4 py-3">Lieu</th>
                      <th className="px-4 py-3 w-28">Créneaux</th>
                      <th className="px-4 py-3">Période</th>
                      <th className="px-4 py-3 w-32">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.places.map(place => {
                      const exists = existingKeys.has(place.key);
                      const first = place.slots[0]?.start.slice(0, 10);
                      const last = place.slots[place.slots.length - 1]?.start.slice(0, 10);
                      return (
                        <tr key={place.key} className="border-t border-[#2D3139]/50 hover:bg-[#1A1D23]/40">
                          <td className="px-4 py-3">
                            <div className="font-medium text-white">{place.name}</div>
                            <div className="text-[10px] text-slate-500 truncate max-w-md">
                              {place.slots[0]?.events[0]?.title}
                              {place.slots.length > 1 && ` +${place.slots.length - 1}`}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-300">{place.slots.length}</td>
                          <td className="px-4 py-3 text-slate-400">
                            {first === last ? formatDateFr(first) : `${formatDateFr(first)} → ${formatDateFr(last)}`}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                              exists
                                ? 'bg-indigo-500/10 border-indigo-500/25 text-indigo-400'
                                : 'bg-emerald-500/10 border-emerald-500/25 text-emerald-400'
                            }`}>
                              {exists ? 'Mise à jour' : 'Nouveau'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Action ────────────────────────────────────── */}
            <div className="mt-5 flex items-center gap-3">
              <button
                onClick={handleImport}
                disabled={importing || parsed.places.length === 0}
                className="flex items-center gap-2 px-4 py-2.5 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl text-xs font-bold transition-colors"
              >
                {importing ? (
                  <><div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Importation…</>
                ) : (
                  <><Map className="w-3.5 h-3.5" /> Importer {parsed.places.length} lieu(x)</>
                )}
              </button>
              <button
                onClick={reset}
                className="px-4 py-2.5 bg-[#1A1D23] border border-[#2D3139] text-slate-300 hover:text-white rounded-xl text-xs font-medium transition-colors"
              >
                Annuler
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
